import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Form, FormProvider, useForm } from "react-hook-form";
import { debounce } from "lodash";
import { NetworkStatus } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";

import Grid from "@mui/material/Grid";
import GlobalStyles from "@mui/material/GlobalStyles";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import RefreshIcon from "@mui/icons-material/Refresh";

import formatPageTitle from "@utils/formatPageTitle";
import { FormSelectField, FormTextField } from "@covertree/ui";
import { TableView } from "@covertree/ui/table/TableView";
import { Toast } from "@covertree/utils";
import {
  SequoiaStatus,
  useQuerySequoiaStatusesQuery,
} from "@covertree/agent-portal-graphql";

import { SequoiaStatusesForm, defaultValues, sourceOptions } from "./form";
import { SequoiaStatusDetails } from "./SequoiaStatusDetails/SequoiaStatusDetails";

export function SequoiaStatuses() {
  const [page, setPage] = useState(1);
  const [nextToken, setNextToken] = useState<string>();
  const [pagingTokens, setPagingTokens] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [debouncedRiskId, setDebouncedRiskId] = useState<string>();

  const [selectedStatus, setSelectedStatus] = useState<SequoiaStatus>();

  const form = useForm<SequoiaStatusesForm>({
    defaultValues,
    mode: "all",
  });

  const { control, watch } = form;
  const watchedSource = watch("source");
  const watchedRiskId = watch("riskId");

  const { data, loading, error, refetch, networkStatus } =
    useQuerySequoiaStatusesQuery({
      variables: {
        source: watchedSource,
        riskId: debouncedRiskId,
        pageSize: 50,
        nextToken,
      },
      notifyOnNetworkStatusChange: true,
    });

  const isLoading = useMemo(
    () => loading || networkStatus === NetworkStatus.refetch,
    [loading, networkStatus]
  );

  const setRiskId = useMemo(
    () =>
      debounce(() => {
        setDebouncedRiskId(watchedRiskId || undefined);
        setNextToken(undefined);
        setPage(1);
        setPagingTokens([undefined]);
      }, 1000),
    [watchedRiskId]
  );

  useEffect(() => {
    setRiskId();
  }, [setRiskId, watchedRiskId]);

  useEffect(() => {
    setNextToken(undefined);
    setPage(1);
    setPagingTokens([undefined]);
  }, [watchedSource]);

  useEffect(() => {
    if (error) {
      Toast.showError(
        "An error occurred while querying the Sequoia statuses. Please reach out to IT support if the problem persists."
      );
      datadogRum.addError(error);
    }
  }, [error]);

  useEffect(() => {
    if (data?.querySequoiaStatuses.nextToken) {
      setPagingTokens((prevTokens) => {
        const newTokens = [...prevTokens];
        newTokens[page] = data.querySequoiaStatuses.nextToken;
        return newTokens;
      });
    }
  }, [data?.querySequoiaStatuses.nextToken, page]);

  return (
    <>
      <GlobalStyles
        styles={{
          ".page-layout-content:not(:has(#banner))": {
            "& .MuiTableContainer-root": {
              maxHeight: "calc(100vh - 445px) !important",
            },
          },
          ".page-layout-content:has(#banner)": {
            "& .MuiTableContainer-root": {
              maxHeight: "calc(100vh - 523px) !important",
            },
          },
        }}
      />
      <Helmet>
        <title>{formatPageTitle("Sequoia Upload Statuses")}</title>
      </Helmet>

      <Form id="sequoia-statuses" control={control}>
        <FormProvider {...form}>
          <Grid container pb={5} spacing={2}>
            <Grid item xs={6}>
              <FormSelectField
                fullWidth
                id="source"
                name="source"
                control={control}
                label="Select Source"
                options={sourceOptions}
                capitalize={true}
              />
            </Grid>
            <Grid item xs={6}>
              <FormTextField
                fullWidth
                control={control}
                label="Search by Risk ID"
                id="riskId"
                name="riskId"
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Form>

      {isLoading && (
        <Grid display="flex" justifyContent="center" paddingTop={5}>
          <CircularProgress size={80} />
        </Grid>
      )}

      {!isLoading && (
        <>
          <Box display="flex" justifyContent="flex-end">
            <RefreshIcon sx={{ cursor: "pointer" }} onClick={() => refetch()} />
          </Box>
          {data && (
            <TableView
              columns={[
                { headerName: "Source" },
                { headerName: "Risk ID" },
                { headerName: "Status" },
                { headerName: "Policy Locator" },
              ]}
              rows={data.querySequoiaStatuses.items.map((item, id) => ({
                id,
                data: [
                  item.trackingSource ?? "N/A",
                  item.trackingId ?? "N/A",
                  item.status,
                  item.policyLocator ?? "N/A",
                ],
                onClickRow: () => {
                  setSelectedStatus(item);
                },
              }))}
              header="Sequoia Statuses"
              onPageChange={(_, p) => {
                setPage(p);
                setNextToken(pagingTokens[p - 1]);
              }}
              page={page}
              totalCount={-1}
            />
          )}
        </>
      )}
      <SequoiaStatusDetails
        data={selectedStatus}
        onClose={() => setSelectedStatus(undefined)}
      />
    </>
  );
}
